// @ts-strict-ignore
import {MPProperties} from 'common/constants';
import {ResourceType} from 'common/report/constants';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {apiClient} from 'common/util/api-client';
import {SuccessResponse} from 'common/util/shared-client';

import {appApiUrl, queryApiUrl} from '../utils';
import {User} from './types';

interface Column {
  value: string;
  resourceType: string;
}

export interface StatsResults {
  webhook: number;
}

export async function fetchUsersCount({
  cohort,
  includeAllUsers,
  abortSignal = null,
  useQuerySampling = false,
}: {
  cohort: Cohort;
  includeAllUsers: boolean;
  abortSignal?: AbortSignal;
  useQuerySampling?: boolean;
}): Promise<SuccessResponse<StatsResults>> {
  const url = queryApiUrl(`engage/stats`);
  const reqBody = {
    filter_by_cohort: {
      raw_cohort: cohort.serialize(),
    },
    ...(cohort.dataGroupId ? {data_group_id: cohort.dataGroupId} : {}),
    workspace_id: getWorkspaceId(),
    project_id: getProjectId(),
    include_all_users: includeAllUsers,
    segment_by_cohorts: {
      webhook: true,
    },
    use_query_sampling: useQuerySampling,
    enable_upsampling: useQuerySampling,
  };
  const response = await apiClient.postNoUnwrap<any>(url, reqBody, {
    headers: {
      'Content-Type': `application/json`,
    },
    abortSignal,
  });
  return response;
}

export async function fetchUsers({
  cohort,
  includeAllUsers,
  columns,
  abortSignal = null,
  search = ``,
  sortOrder = `descending`,
  sortColumn = {
    value: MPProperties.LastSeen,
    resourceType: ResourceType.User,
  },
  useQuerySampling = false,
  limit = 1000,
}: {
  cohort: Cohort;
  includeAllUsers: boolean;
  columns: Column[];
  abortSignal?: AbortSignal;
  search?: string;
  sortOrder?: string;
  sortColumn?: Column;
  useQuerySampling?: boolean;
  limit?: number;
}) {
  const url = queryApiUrl(`engage`);
  const reqBody = {
    filter_by_cohort: {
      raw_cohort: cohort.serialize(),
    },
    workspace_id: getWorkspaceId(),
    sort_order: sortOrder,
    sort_column: sortColumn,
    search,
    columns,
    // https://www.notion.so/mxpnl/Query-tracking-spec-da125612f4b04f24b9823dc7d28232c3
    // for Event & Users pages is_main_query_for_report need to be true for main "querier" pages
    tracking_props: {
      is_main_query_for_report: true,
      report_name: `users_sidebar`,
    },
    project_id: getProjectId(),
    include_all_users: includeAllUsers,
    limit,
    ...(cohort.dataGroupId ? {data_group_id: cohort.dataGroupId} : {}),
    use_query_sampling: useQuerySampling,
  };
  return apiClient.post<User[]>(url, reqBody, {
    headers: {
      'Content-Type': `application/json`,
    },
    abortSignal,
  });
}

export async function downloadCSV({
  includeAllUsers,
  queryHref,
  search,
  columns,
  cohort,
}: {
  queryHref: string;
  cohort: Cohort;
  search?: string;
  columns: Column[];
  includeAllUsers: boolean;
}): Promise<string> {
  const url = appApiUrl(`export_csv`, {projectId: getProjectId(), workspaceId: getWorkspaceId()});
  const reqBody = {
    query_href: queryHref,
    columns,
    filter_by_cohort: {
      raw_cohort: cohort.serialize(),
    },
    search,
    include_all_users: includeAllUsers,
    ...(cohort.dataGroupId ? {data_group_id: cohort.dataGroupId} : {}),
  };
  const response = await apiClient.post<string>(url, JSON.stringify(reqBody));
  const {results} = JSON.parse(response);
  if (!results) {
    throw new Error(`No results found`);
  }
  return results.url;
}
